<loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

<main>
  <header>
    <div class="headline">
      <h1>{{ 'Merge_Lot_Items_Detail_Title' | translateWF: 'Merge Lot Items' }}</h1>
    </div>
    <div class="buttons">
      @if (canEdit()) {
        <p-button [label]="'Common_Save' | translateWF: 'Save'" (click)="save()" [disabled]="!canSave()" />
      }
      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="cancel()" />
    </div>
  </header>

  <div class="content">
    <section>
      <div class="grid grid-cols-12 gap-8">
        <!-- Receiving lot item -->
        <div class="col-span-12 md:col-span-4">
          <h2 class="mb-4">{{ 'Merge_Lot_Items_Receiving_Lot_Item' | translateWF: 'Receiving Lot Item' }}</h2>
          <label class="mb-2" for="receiverSearch">{{ 'Merge_Lot_Items_Lot_Item' | translateWF: 'Lot Item' }}</label>
          <p-autocomplete
            [inputId]="'receiverSearch'"
            fluid
            [suggestions]="receiverOptions()"
            (completeMethod)="searchReceiver($event)"
            field="number"
            [formControl]="receiverSearch"
            class="w-full"
            styleClass="w-full"
            (onSelect)="onReceiverSelected($event)"
            [showClear]="true"
            (onClear)="onReceiverSelected()"
            (onKeyUp)="cancelShortSearch($event, 'receiver')"
            [delay]="300"
            [minLength]="searchMinLength"
            [forceSelection]="true"
            [placeholder]="'Merge_Lot_Items_Search_Placeholder' | translateWF: 'Type part of the lot number'"
          >
            <ng-template let-lot #item>
              <div class="flex w-full justify-between gap-4">
                <span class="font-medium">{{ lot.number }}</span>
                <span class="text-gray-500">{{ lot.netQuantity | number: '1.2-2' }} · {{ lot.currentLocationCode }}</span>
              </div>
            </ng-template>
          </p-autocomplete>
          <small class="text-gray-500">
            {{ 'Merge_Lot_Items_Receiver_Hint' | translateWF: 'Output lot item of a Completed batch.' }}
          </small>
        </div>

        <!-- Summary -->
        <div class="col-span-12 md:col-span-4">
          <h2 class="mb-4">{{ 'Common_Summary' | translateWF: 'Summary' }}</h2>
          @if (receiver(); as lotItem) {
            <div class="form-box-row justify-between mb-2">
              <span class="font-medium">{{ 'Common_Item_Code' | translateWF: 'Item Code' }}</span>
              <span class="text-gray-600">{{ lotItem.itemCode }}</span>
            </div>
            <div class="form-box-row justify-between mb-2">
              <span class="font-medium">{{ 'Merge_Lot_Items_Batch' | translateWF: 'Batch' }}</span>
              <span class="text-gray-600">
                {{ receiverBatch()?.number || '' }}
                <p-tag class="ml-2" [value]="batchStatusLabel(receiverBatch())" [severity]="isReceiverBatchValid() ? 'success' : 'danger'" />
              </span>
            </div>
            <div class="form-box-row justify-between mb-2">
              <span class="font-medium">{{ 'Merge_Lot_Items_Original_Net_Quantity' | translateWF: 'Original Net Quantity' }}</span>
              <span class="text-gray-600">{{ lotItem.originalNetQuantity | number: '1.2-2' }}</span>
            </div>
            <div class="form-box-row justify-between mb-2">
              <span class="font-medium">{{ 'Merge_Lot_Items_Current_Net_Quantity' | translateWF: 'Current Net Quantity' }}</span>
              <span class="text-gray-600">{{ lotItem.netQuantity | number: '1.2-2' }}</span>
            </div>
            <div class="form-box-row justify-between mb-2">
              <span class="font-medium">{{ 'Merge_Lot_Items_Net_After_Merge' | translateWF: 'Net Quantity After Merge' }}</span>
              <span class="text-gray-600">{{ lotItem.netQuantity + totalToMerge() | number: '1.2-2' }}</span>
            </div>
            @if (!lotItem.batchReference) {
              <p-message severity="warn" class="mt-2">
                {{ 'Merge_Lot_Items_No_Batch' | translateWF: 'The lot item does not belong to a batch, so it cannot be merged.' }}
              </p-message>
            } @else if (!isReceiverBatchValid() && receiverBatch()) {
              <p-message severity="warn" class="mt-2">
                {{ 'Merge_Lot_Items_Receiver_Batch_Invalid' | translateWF: 'The receiving lot item must belong to a Completed batch.' }}
              </p-message>
            }
          } @else {
            <span class="text-gray-500">{{ 'Merge_Lot_Items_Select_Receiver' | translateWF: 'Select the receiving lot item.' }}</span>
          }
        </div>
      </div>
    </section>

    <!-- Source lot items -->
    @if (canEdit()) {
      <section>
        <div class="under__line mb-[2rem] flex justify-between items-center">
          <h2>{{ 'Merge_Lot_Items_Source_Lot_Items' | translateWF: 'Source Lot Items' }}</h2>
          <p-button
            [label]="'Merge_Lot_Items_Add_Source' | translateWF: 'Add Source Lot'"
            icon="pi pi-plus"
            variant="outlined"
            (click)="addSourceRow()"
            [disabled]="!receiver() || !isReceiverBatchValid()"
          />
        </div>

        <p-table [value]="sourceRows()" styleClass="cs-datatable-overflow-visible">
          <ng-template #header>
            <tr>
              <th>{{ 'Merge_Lot_Items_Source_Lot_Item' | translateWF: 'Source Lot Item' }}</th>
              <th>{{ 'Merge_Lot_Items_Batch' | translateWF: 'Batch' }}</th>
              <th>{{ 'Merge_Lot_Items_Available' | translateWF: 'Available' }}</th>
              <th>{{ 'Merge_Lot_Items_Quantity_To_Merge' | translateWF: 'Quantity to Merge' }}</th>
              <th>{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
            </tr>
          </ng-template>
          <ng-template #body let-row let-i="rowIndex">
            <tr [formGroup]="row.form">
              <td>
                <p-autocomplete
                  fluid
                  [suggestions]="sourceOptions()"
                  (completeMethod)="searchSource($event)"
                  field="number"
                  formControlName="source"
                  appendTo="body"
                  (onSelect)="onSourceSelected(row, $event)"
                  [showClear]="true"
                  (onClear)="onSourceSelected(row)"
                  (onKeyUp)="cancelShortSearch($event, 'source')"
                  [delay]="300"
                  [minLength]="searchMinLength"
                  [forceSelection]="true"
                  [placeholder]="'Merge_Lot_Items_Search_Placeholder' | translateWF: 'Type part of the lot number'"
                >
                  <ng-template let-lot #item>
                    <div class="flex w-full justify-between gap-4">
                      <span class="font-medium">{{ lot.number }}</span>
                      <span class="text-gray-500">{{ lot.netQuantity | number: '1.2-2' }} · {{ lot.currentLocationCode }}</span>
                    </div>
                  </ng-template>
                </p-autocomplete>
              </td>
              <td>
                @if (row.lotItem) {
                  {{ batchFor(row.lotItem)?.number || '—' }}
                  <p-tag class="ml-2" [value]="batchStatusLabel(batchFor(row.lotItem))" [severity]="isSourceBatchValid(row.lotItem) ? 'success' : 'danger'" />
                  @if (!row.lotItem.batchReference) {
                    <small class="block text-red-500">{{ 'Merge_Lot_Items_No_Batch' | translateWF: 'The lot item does not belong to a batch, so it cannot be merged.' }}</small>
                  }
                }
              </td>
              <td>{{ row.lotItem ? (row.lotItem.netQuantity | number: '1.2-2') : '' }}</td>
              <td>
                <input pInputText type="number" formControlName="quantity" min="0" step="0.01" />
                @if (row.form.get('quantity')?.hasError('exceedsAvailable')) {
                  <small class="block text-red-500">{{ 'Merge_Lot_Items_Exceeds_Available' | translateWF: 'Exceeds the available quantity' }}</small>
                }
              </td>
              <td>
                <p-button icon="pi pi-minus" variant="text" size="small" (click)="removeSourceRow(i)" />
              </td>
            </tr>
          </ng-template>
        </p-table>

        @if (sourceRows().length === 0) {
          <div class="text-center py-8 text-gray-500">
            {{ 'Merge_Lot_Items_No_Sources' | translateWF: 'Add the output lot items of a Closed batch to merge into the receiving lot item.' }}
          </div>
        }
      </section>
    }

    <!-- Merge history -->
    <section>
      <div class="under__line mb-[2rem] flex justify-between items-center">
        <h2>{{ 'Merge_Lot_Items_History' | translateWF: 'Merge History' }}</h2>
      </div>
      <p-table [value]="mergeHistory()" styleClass="cs-datatable-overflow-visible">
        <ng-template #header>
          <tr>
            <th>{{ 'Merge_Lot_Items_Source_Lot_Item' | translateWF: 'Source Lot Item' }}</th>
            <th>{{ 'Merge_Lot_Items_Merged_Quantity' | translateWF: 'Merged Quantity' }}</th>
            <th>{{ 'Common_Date' | translateWF: 'Date' }}</th>
            <th>{{ 'Common_User' | translateWF: 'User' }}</th>
          </tr>
        </ng-template>
        <ng-template #body let-row>
          <tr>
            <td>{{ row.sourceNumber }}</td>
            <td>{{ row.entry.quantity | number: '1.2-2' }}</td>
            <td>{{ row.entry.date | date: 'short' }}</td>
            <td>{{ row.entry.user?.username }}</td>
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr>
            <td colspan="4" class="text-center text-gray-500">{{ 'Merge_Lot_Items_No_History' | translateWF: 'No merges yet.' }}</td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  </div>
</main>
